import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialText.css';
import { useNavigate } from './Router';

const SocialText = ({ text }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  
  // Définir la limite de caractères pour la prévisualisation (environ 3-4 lignes de texte)
  const CHARACTER_LIMIT = 280;
  
  // Vérifier si le texte dépasse la limite et nécessite un bouton "Voir plus"
  const needsExpansion = text && text.length > CHARACTER_LIMIT;
  
  // Mémoriser la version tronquée du texte pour éviter des calculs inutiles
  const truncatedText = useMemo(() => {
    if (!text || !needsExpansion) return text;
    
    // Trouver un point de coupure propre (fin de phrase ou espace)
    let cutoffIndex = CHARACTER_LIMIT;
    while (cutoffIndex > CHARACTER_LIMIT - 50 && cutoffIndex < text.length) {
      if (text[cutoffIndex] === '.' || text[cutoffIndex] === ' ' || text[cutoffIndex] === '\n') {
        break;
      }
      cutoffIndex++;
    }
    
    // Si on n'a pas trouvé de point de coupure propre, revenir à la limite stricte
    if (cutoffIndex >= CHARACTER_LIMIT + 50) {
      cutoffIndex = CHARACTER_LIMIT;
    }
    
    return text.substring(0, cutoffIndex);
  }, [text]);
  
  if (!text) return null;

  // Fonction pour transformer le contenu avec gestion des liens, mentions, hashtags, etc.
  const transformContent = (contentToTransform) => {
    // Expressions régulières pour détecter différents types de contenu
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const referralRegex = /#\/(\S+)/g;
    const mentionRegex = /@([a-zA-Z0-9._]+)/g;
    const hashtagRegex = /#([a-zA-Z0-9_]+)/g;
    const breakRegex = /\[br\]|\n/g;
    
    // Diviser le texte en fragments
    let parts = [];
    let lastIndex = 0;
    
    // Fonction pour ajouter un fragment de texte entre deux matches
    const addTextPart = (endIndex) => {
      if (endIndex > lastIndex) {
        const textPart = contentToTransform.substring(lastIndex, endIndex);
        if (textPart) {
          parts.push({ type: 'text', content: textPart });
        }
      }
    };
    
    // Trouver toutes les correspondances et les positions
    const matches = [
      ...findMatches(contentToTransform, urlRegex, 'url'),
      ...findMatches(contentToTransform, referralRegex, 'referral'),
      ...findMatches(contentToTransform, mentionRegex, 'mention'),
      ...findMatches(contentToTransform, hashtagRegex, 'hashtag'),
      ...findMatches(contentToTransform, breakRegex, 'break')
    ].sort((a, b) => a.index - b.index);
    
    // Traiter chaque correspondance
    matches.forEach(match => {
      addTextPart(match.index);
      parts.push({
        type: match.type,
        content: match.content,
        value: match.value
      });
      lastIndex = match.index + match.content.length;
    });
    
    // Ajouter le texte restant après la dernière correspondance
    addTextPart(contentToTransform.length);
    
    // Générer les éléments React pour chaque partie
    return parts.map((part, index) => {
      switch (part.type) {
        case 'url':
          if (part.content.startsWith('https://peupleqc.com')) {
            const path = part.content.replace('https://peupleqc.com', '');
            return (
              <button
                key={index}
                className="text-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(path || '/');
                }}
              >
                {formatUrl(part.content)}
              </button>
            );
          }
          return (
            <a 
              key={index} 
              href={part.content} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-link"
            >
              {formatUrl(part.content)}
            </a>
          );
        case 'referral':
          return (
            <button
              key={index}
              className="text-referral"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/${part.value}`);
              }}
            >
              Voir plus
            </button>
          );
        case 'mention':
          return (
            <a
              key={index}
              href="#"
              className="text-mention"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/user/${part.value}`);
              }}
            >
              @{part.value}
            </a>
          );
        case 'hashtag':
          return (
            <a
              key={index}
              href="#"
              className="text-hashtag"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/hashtag/${part.value}`);
              }}
            >
              #{part.value}
            </a>
          );
        case 'break':
          return <br key={index} />;
        case 'text':
        default:
          return <React.Fragment key={index}>{part.content}</React.Fragment>;
      }
    });
  };
  
  // Fonction pour trouver toutes les correspondances d'une expression régulière
  const findMatches = (text, regex, type) => {
    const matches = [];
    let match;
    regex.lastIndex = 0; // Réinitialiser l'index de recherche
    
    while ((match = regex.exec(text)) !== null) {
      let value = match[1]; // Capture group
      
      // Pour les sauts de ligne et les [br]
      if (type === 'break') {
        value = '';
      }
      
      matches.push({
        type,
        content: match[0], // Full match
        value,
        index: match.index
      });
    }
    
    return matches;
  };
  
  // Fonction pour formater les URLs (affichage simplifié)
  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      let formattedUrl = urlObj.hostname;
      
      // Ajouter le chemin s'il existe et n'est pas juste "/"
      if (urlObj.pathname && urlObj.pathname !== '/') {
        // Limiter la longueur du chemin
        const pathDisplay = urlObj.pathname.length > 15 
          ? urlObj.pathname.substring(0, 15) + '...' 
          : urlObj.pathname;
        formattedUrl += pathDisplay;
      }
      
      return formattedUrl;
    } catch (e) {
      // Fallback en cas d'URL invalide
      return url.length > 30 ? url.substring(0, 30) + '...' : url;
    }
  };

  // Contenu à afficher en fonction de l'état d'expansion
  const contentToDisplay = expanded || !needsExpansion ? text : truncatedText;

  return (
    <div className="post-text">
      {transformContent(contentToDisplay)}
      
      {/* Bouton "Voir plus" si nécessaire */}
      {needsExpansion && !expanded && (
        <div className="text-see-more-wrapper">
          <button
            className="text-see-more-btn"
            onClick={() => setExpanded(true)}
          >
            Voir plus
          </button>
        </div>
      )}
      
      {/* Bouton "Voir moins" quand le texte est développé */}
      {needsExpansion && expanded && (
        <div className="text-see-more-wrapper">
          <button
            className="text-see-more-btn"
            onClick={() => setExpanded(false)}
          >
            Voir moins
          </button>
        </div>
      )}
    </div>
  );
};

export { SocialText };